import { buildCsvImportAuthHeaders } from '../import-engine/csvImportPersistenceSync';
import { resetPersistedCsvTradesCache } from '../import-engine/persistedCsvTradeRead';

export type ManualTradeInput = {
  accountId: string;
  date: string;
  time?: string;
  exitDate?: string;
  exitTime?: string;
  timeZone: string;
  status: 'open' | 'closed';
  side: 'Long' | 'Short';
  symbol: string;
  assetType: 'stocks' | 'options' | 'futures' | 'forex' | 'crypto';
  quantity: number;
  entryPrice?: number;
  exitPrice?: number;
  grossPnl?: number;
  grossPnlSource: 'manual' | 'calculated' | 'missing';
  commissions: number;
  otherFees: number;
  netPnl?: number;
  netPnlSource: 'manual' | 'calculated' | 'missing';
  contractMultiplier?: number;
  optionRight?: 'call' | 'put';
  strike?: number;
  expiration?: string;
  notes?: string;
  tags?: string[];
  idempotencyKey: string;
};

export type ManualTradeDeleteTarget = { tradeId: string; accountId: string };
export type PersistedTradeDeleteTarget = ManualTradeDeleteTarget & { sourceType: 'manual' | 'csv' };

function getApiBase(): string {
  return String((import.meta as any).env?.VITE_API_BASE || '').trim().replace(/\/+$/, '');
}

export async function createManualTrade(input: ManualTradeInput): Promise<{ tradeId: string; accountName: string; created: boolean }> {
  const response = await fetch(`${getApiBase()}/api/trades/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await buildCsvImportAuthHeaders()) },
    body: JSON.stringify(input),
  });
  let result: any = null;
  try { result = await response.json(); } catch {}
  if (!response.ok) {
    // The backend's `error` field is often a stable machine code (for example,
    // manual_trade_invalid); prefer its field-specific detail for the user.
    if (result?.detail || result?.message || result?.error) throw new Error(String(result.detail || result.message || result.error));
    if (response.status === 404) throw new Error('The backend does not recognize the manual trade save endpoint (HTTP 404). Restart the staging backend with the current application code.');
    throw new Error(`Manual trade save failed (HTTP ${response.status}).`);
  }
  if (result?.ok !== true || !result?.tradeId || result.accountId !== input.accountId) {
    throw new Error('The server returned an incomplete or mismatched save confirmation for the selected trading account.');
  }
  resetPersistedCsvTradesCache();
  return { tradeId: String(result.tradeId), accountName: String(result.accountName || 'Trading account'), created: result.created === true };
}

export async function updateManualTrade(tradeId: string, input: Omit<ManualTradeInput, 'idempotencyKey'>): Promise<void> {
  if (!/^manual-[0-9a-f-]{36}$/i.test(tradeId)) throw new Error('Select a saved manual trade to edit.');
  const response = await fetch(`${getApiBase()}/api/trades/manual/${encodeURIComponent(tradeId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', ...(await buildCsvImportAuthHeaders()) },
    body: JSON.stringify(input),
  });
  let result: any = null;
  try { result = await response.json(); } catch {}
  if (!response.ok) throw new Error(String(result?.detail || result?.message || result?.error || `Manual trade update failed (HTTP ${response.status}).`));
  if (result?.ok !== true || result.tradeId !== tradeId || result.accountId !== input.accountId) {
    throw new Error('The server did not confirm an update to this trade and account. Refresh before trying again.');
  }
  resetPersistedCsvTradesCache();
}

export async function updateTradeInitialRisk(tradeId: string, input: { amount: number | null; method: 'amount' | 'stopPrice' | null; stopPrice: number | null }): Promise<void> {
  const id = String(tradeId || '').trim();
  if (!id || id.length > 256) throw new Error('Select a saved trade to update.');
  const response = await fetch(`${getApiBase()}/api/trades/${encodeURIComponent(id)}/initial-risk`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', ...(await buildCsvImportAuthHeaders()) },
    body: JSON.stringify(input),
  });
  let result: any = null;
  try { result = await response.json(); } catch {}
  if (!response.ok) throw new Error(String(result?.detail || result?.message || result?.error || `Initial Risk save failed (HTTP ${response.status}).`));
  if (result?.ok !== true || result.tradeId !== id || result.initialRisk !== input.amount) {
    throw new Error('The server did not confirm the Initial Risk update. Refresh before trying again.');
  }
  resetPersistedCsvTradesCache();
}

export async function deleteManualTrades(trades: ManualTradeDeleteTarget[]): Promise<{ deletedIds: string[] }> {
  const unique = new Map<string, ManualTradeDeleteTarget>();
  for (const target of trades) {
    const tradeId = String(target?.tradeId || '').trim();
    const accountId = String(target?.accountId || '').trim();
    if (!tradeId || !accountId) throw new Error('The selected manual trades could not be validated. Refresh the Trades page and try again.');
    unique.set(`${accountId}\u0000${tradeId}`, { tradeId, accountId });
  }
  const targets = [...unique.values()];
  if (targets.length === 0 || targets.length > 500) throw new Error('Select between 1 and 500 manual trades to delete.');

  const response = await fetch(`${getApiBase()}/api/trades/manual`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...(await buildCsvImportAuthHeaders()) },
    body: JSON.stringify({ trades: targets }),
  });
  let result: any = null;
  try { result = await response.json(); } catch {}
  if (!response.ok) throw new Error(String(result?.detail || result?.message || result?.error || `Manual trade deletion failed (HTTP ${response.status}).`));

  const deletedIds = Array.isArray(result?.deletedIds) ? result.deletedIds.map((id: unknown) => String(id)) : [];
  const expectedIds = targets.map((target) => target.tradeId).sort();
  if (result?.ok !== true || Number(result.deletedCount) !== expectedIds.length || deletedIds.slice().sort().join('\u0000') !== expectedIds.join('\u0000')) {
    throw new Error('The server did not confirm removal of every selected manual trade. Refresh the Trades page and try again.');
  }

  resetPersistedCsvTradesCache();
  return { deletedIds };
}

export async function deleteSelectedPersistedTrades(trades: PersistedTradeDeleteTarget[]): Promise<{ deletedIds: string[] }> {
  const targets = trades.map((target) => ({
    tradeId: String(target?.tradeId || '').trim(),
    accountId: String(target?.accountId || '').trim(),
    sourceType: target?.sourceType,
  }));
  const keys = new Set(targets.map((target) => `${target.accountId}\u0000${target.tradeId}`));
  if (targets.length === 0 || targets.length > 500 || keys.size !== targets.length
      || targets.some((target) => !target.tradeId || !target.accountId || !['manual', 'csv'].includes(target.sourceType))) {
    throw new Error('The selected persisted trades could not be validated. Refresh the Trades page and try again.');
  }

  const response = await fetch(`${getApiBase()}/api/trades/selected`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...(await buildCsvImportAuthHeaders()) },
    body: JSON.stringify({ trades: targets }),
  });
  let result: any = null;
  try { result = await response.json(); } catch {}
  if (!response.ok) throw new Error(String(result?.detail || result?.message || result?.error || `Trade deletion failed (HTTP ${response.status}).`));

  const deletedIds = Array.isArray(result?.deletedIds)
    ? result.deletedIds.map((item: any) => String(typeof item === 'object' ? item?.tradeId : item || ''))
    : [];
  const expectedIds = targets.map((target) => target.tradeId).sort();
  if (result?.ok !== true || Number(result.deletedCount) !== expectedIds.length || deletedIds.slice().sort().join('\u0000') !== expectedIds.join('\u0000')) {
    throw new Error('The server did not confirm removal of every selected trade. Refresh the Trades page and try again.');
  }

  resetPersistedCsvTradesCache();
  return { deletedIds };
}
